import { format, subMonths } from 'date-fns'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSearchParams } from 'react-router'

import { Skeleton, SkeletonRows } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Grain, useQuoteAnalyticsSummary } from '@/models/quoteAnalytics'
import { AccuracyTab } from './AccuracyTab'
import { FunnelTab } from './FunnelTab'
import { OverviewTab } from './OverviewTab'
import { QuoteAnalyticsFilters } from './QuoteAnalyticsFilters'
import { QuotesListTab } from './QuotesListTab'

const ISO = (d: Date) => format(d, 'yyyy-MM-dd')

export function QuotesAnalytics() {
  const [params, setParams] = useSearchParams()
  const grain = (params.get('grain') as Grain | null) || Grain.INDICATION
  const dateFrom = params.get('date_from') || ISO(subMonths(new Date(), 1))
  const dateTo = params.get('date_to') || ISO(new Date())
  const excludeTestOrgs = params.get('exclude_test_orgs') !== 'false'

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params)
    next.set(k, v)
    setParams(next, { replace: true })
  }

  const onDateRange = (from: string, to: string) => {
    const next = new URLSearchParams(params)
    next.set('date_from', from)
    next.set('date_to', to)
    setParams(next, { replace: true })
  }

  const onExcludeTestOrgs = (v: boolean) => {
    const next = new URLSearchParams(params)
    if (!v) {
      next.set('exclude_test_orgs', 'false')
    } else {
      next.delete('exclude_test_orgs')
    }
    setParams(next, { replace: true })
  }

  const filters = {
    grain,
    granularity: 'month' as const,
    dateFrom,
    dateTo,
    excludeTestOrgs,
  }
  const { data: summary, isLoading } = useQuoteAnalyticsSummary(filters)
  const [tab, setTab] = useState('overview')

  return (
    <>
      <Helmet>
        <title>Quotes Analytics</title>
      </Helmet>
      <div className="flex flex-col gap-6 py-6 md:p-6">
        <h1 className="text-2xl font-semibold">Quotes Analytics</h1>
        <QuoteAnalyticsFilters
          grain={grain}
          onGrain={(g) => setParam('grain', g)}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateRange={onDateRange}
          excludeTestOrgs={excludeTestOrgs}
          onExcludeTestOrgs={onExcludeTestOrgs}
        />
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="funnel">Funnel</TabsTrigger>
            <TabsTrigger value="accuracy">Accuracy</TabsTrigger>
            <TabsTrigger value="quotes">Quotes</TabsTrigger>
          </TabsList>
          {isLoading || !summary ? (
            <div className="mt-4 flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-24 w-full" />
                ))}
              </div>
              <Skeleton className="h-64 w-full" />
              <SkeletonRows rows={4} />
            </div>
          ) : (
            <>
              <TabsContent value="overview">
                <OverviewTab summary={summary} />
              </TabsContent>
              <TabsContent value="funnel">
                <FunnelTab summary={summary} />
              </TabsContent>
              <TabsContent value="accuracy">
                <AccuracyTab accuracy={summary.accuracy} />
              </TabsContent>
              <TabsContent value="quotes">
                <QuotesListTab filters={filters} />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </>
  )
}
